import type { LucideIcon } from 'lucide-react';
import {
  Antenna,
  Box,
  Building2,
  Cable,
  CircuitBoard,
  Cpu,
  FlaskConical,
  Layers,
  Package,
  Router,
  Satellite,
  Thermometer,
  Wifi,
  Zap,
} from 'lucide-react';
import type { ValueChainNode, ValueChainNodeType } from '@/types/valueChain';

export type ImportanceLevel = 'Very High' | 'High' | 'Medium' | 'Low';

const TYPE_ICON: Record<ValueChainNodeType, LucideIcon> = {
  'Product / System': Package,
  'Assembly / Module': CircuitBoard,
  Component: Cpu,
  Device: Cpu,
  'Layer / Structure': Layers,
  Material: Layers,
  'Chemical / Dopant': FlaskConical,
  Company: Building2,
};

const TYPE_TONE: Record<ValueChainNodeType, string> = {
  'Product / System': 'bg-slate-100 text-slate-700',
  'Assembly / Module': 'bg-blue-50 text-blue-700',
  Component: 'bg-gray-100 text-gray-700',
  Device: 'bg-indigo-50 text-indigo-700',
  'Layer / Structure': 'bg-sky-50 text-sky-700',
  Material: 'bg-amber-50 text-amber-800',
  'Chemical / Dopant': 'bg-teal-50 text-teal-800',
  Company: 'bg-violet-50 text-violet-700',
};

export function shortName(name: string): string {
  const parts = name
    .split(/\s*\/\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return name;
  const compact = parts.find((part) => part.length <= 42) ?? parts[0];
  return compact.replace(/\s+/g, ' ');
}

export function nodeIcon(node: ValueChainNode): LucideIcon {
  const hay = node.name.toLowerCase();
  if (/\b(phased|antenna|dish|uta)\b/.test(hay)) return Antenna;
  if (/\b(router|wi-?fi|wifi)\b/.test(hay)) return Router;
  if (/\b(power supply|poe|psu|voltage)\b/.test(hay)) return Zap;
  if (/\bcable\b/.test(hay)) return Cable;
  if (/\b(kickstand|stand|mount)\b/.test(hay)) return Box;
  if (/\b(pcb|board|electronics|asic|soc|ic)\b/.test(hay)) return CircuitBoard;
  if (/\b(thermal|heat|backplate|spreader|fin)\b/.test(hay)) return Thermometer;
  if (/\b(radome|housing|enclosure)\b/.test(hay)) return Box;
  if (/\b(satellite|starlink)\b/.test(hay) && node.type === 'Product / System') return Satellite;
  if (/\bwi-?fi\b/.test(hay)) return Wifi;
  return TYPE_ICON[node.type] ?? Cpu;
}

export function nodeTone(node: ValueChainNode): string {
  return TYPE_TONE[node.type] ?? 'bg-gray-100 text-gray-700';
}

export function importanceFromRank(rank?: number | null): ImportanceLevel | null {
  if (rank == null || !Number.isFinite(rank)) return null;
  if (rank <= 2) return 'Very High';
  if (rank <= 4) return 'High';
  if (rank <= 6) return 'Medium';
  return 'Low';
}

export function importanceTone(level: ImportanceLevel): string {
  if (level === 'Very High') return 'text-emerald-700';
  if (level === 'High') return 'text-emerald-600';
  if (level === 'Medium') return 'text-amber-600';
  return 'text-gray-400';
}

export function importanceFill(level: ImportanceLevel): string {
  if (level === 'Very High') return 'bg-emerald-500';
  if (level === 'High') return 'bg-emerald-400';
  if (level === 'Medium') return 'bg-amber-400';
  return 'bg-gray-300';
}

export function costDots(rank?: number | null): number {
  if (rank == null) return 0;
  return Math.max(1, Math.min(5, 6 - rank));
}

export function evidenceLabel(status?: string): string {
  if (!status) return 'Unstated';
  if (status === 'confirmed') return 'Confirmed';
  if (status === 'inferred') return 'Inferred';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function companyInitials(name: string): string {
  const cleaned = name.replace(/\(.*?\)/g, '').trim();
  const words = cleaned.split(/\s+/).filter((word) => !/^(and|&|of|the)$/i.test(word));
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] ?? ''}${words[1][0] ?? ''}`.toUpperCase();
}
